import { useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { api } from '../api/client';
import { useAuth } from '../context/AuthContext.jsx';
import { Modal, Field, Badge, Empty } from '../components/ui.jsx';
import { t } from '../i18n/index.jsx';
import DataTable from '../components/DataTable.jsx';
import { IconPlus } from '../components/icons.jsx';
import { invalidate } from '../api/cache.js';

const ROLES = ['admin', 'manager', 'partner'];
const blank = { name: '', email: '', role: 'manager', password: '', is_active: 1 };

const roleTemplate = (u) => (
  <Badge tone={u.role === 'admin' ? 'red' : u.role === 'manager' ? 'blue' : 'gray'}>{t(u.role)}</Badge>
);
const statusTemplate = (u) => (
  Number(u.is_active) ? <Badge tone="green">{t('active')}</Badge> : <Badge tone="gray">{t('disabled')}</Badge>
);
const userColumns = [
  { field: 'name', headerText: t('Name'), width: 160 },
  { field: 'email', headerText: t('Email'), width: 220 },
  { headerText: t('Role'), width: 110, template: roleTemplate },
  { headerText: t('Status'), width: 110, template: statusTemplate },
];

export default function Users() {
  const { user } = useAuth();
  const [list, setList] = useState(null);
  const [editing, setEditing] = useState(null);

  async function load() { setList((await api.get('/users')).data); }
  useEffect(() => { load(); }, []);

  if (user.role !== 'admin') return <Navigate to="/" replace />;

  return (
    <div className="page fade-in">
      <div className="page-head">
        <h1 className="page-title">{t('Users')}</h1>
        <button className="btn primary" onClick={() => setEditing({ ...blank })}>
          <IconPlus width={15} height={15} /> {t('New user')}
        </button>
      </div>

      {!list ? (
        <DataTable data={[]} columns={userColumns} loading />
      ) : list.length === 0 ? (
        <Empty>{t('No users.')}</Empty>
      ) : (
        <DataTable data={list} columns={userColumns} onRowClick={setEditing} onRowAction={setEditing} />
      )}

      {editing && (
        <UserForm initial={editing} onClose={() => setEditing(null)} onSaved={() => { invalidate('/users'); setEditing(null); load(); }} />
      )}
    </div>
  );
}

function UserForm({ initial, onClose, onSaved }) {
  const [form, setForm] = useState({ ...initial, password: '' });
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);
  const isNew = !initial.id;
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  async function save() {
    setBusy(true); setErrors({});
    try {
      const payload = {
        name: form.name,
        role: form.role,
        is_active: Number(form.is_active) ? 1 : 0,
      };
      if (form.password) payload.password = form.password;
      if (isNew) {
        await api.post('/users', { ...payload, email: form.email, password: form.password });
      } else {
        await api.put(`/users/${initial.id}`, payload);
      }
      onSaved();
    } catch (err) { setErrors(err.fields || { _: err.message }); }
    finally { setBusy(false); }
  }

  return (
    <Modal title={isNew ? t('New user') : t('Edit {name}', { name: initial.name })} onClose={onClose}
      footer={
        <div className="modal-actions">
          <div className="spacer" />
          <button className="btn ghost" onClick={onClose}>{t('Cancel')}</button>
          <button className="btn primary" onClick={save} disabled={busy}>{busy ? t('Saving…') : t('Save')}</button>
        </div>
      }>
      {errors._ && <div className="alert error">{errors._}</div>}
      <Field label={t('Name')} error={errors.name}><input value={form.name} onChange={set('name')} /></Field>
      <Field label={t('Email')} error={errors.email}>
        <input value={form.email} onChange={set('email')} disabled={!isNew} />
      </Field>
      <div className="grid-2">
        <Field label={t('Role')}><select value={form.role} onChange={set('role')}>{ROLES.map((r) => <option key={r} value={r}>{t(r)}</option>)}</select></Field>
        {!isNew && (
          <Field label={t('Status')}>
            <select value={Number(form.is_active) ? '1' : '0'} onChange={(e) => setForm({ ...form, is_active: e.target.value })}>
              <option value="1">{t('Active')}</option>
              <option value="0">{t('Disabled')}</option>
            </select>
          </Field>
        )}
      </div>
      <Field label={isNew ? t('Password') : t('Reset password (leave blank to keep)')} error={errors.password}>
        <input type="password" value={form.password} onChange={set('password')} autoComplete="new-password" placeholder={isNew ? '' : '••••••••'} />
      </Field>
    </Modal>
  );
}
